import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, FileText, LineChart, Shield, Sprout, CreditCard } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { FavoriteFeatures } from '../components/FavoriteFeatures';
import { getCreditBalanceCache, setCreditBalanceCache, getMyDraftsCache, setMyDraftsCache, clearMyDraftsCache } from '../lib/cache';
import { useToast } from '../context/ToastContext';
import { Mutex } from 'async-mutex';

interface CreditTransaction {
  id: string;
  timestamp: string;
  featureName: string;
  creditChange: number;
  description: string;
}

type RecentFeature = {
  id: string;
  name: string;
  usedAt: string;
  icon: string;
};

const iconComponents = {
  LineChart,
  FileText,
  Building2,
  Shield,
  Sprout,
};

const groupIconMap: Record<string, keyof typeof iconComponents> = {
  'market': 'LineChart',
  'leasing': 'FileText',
  'finance': 'Building2',
  'property': 'Building2',
  'risk': 'Shield',
  'specialty': 'Sprout',
};

const DEFAULT_ICON = 'Building2';

// Rate limiting
const FETCH_INTERVAL = 300000; // 5 minutes
const creditsMutex = new Mutex();

export function Dashboard() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [recentFeatures, setRecentFeatures] = useState<RecentFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedCredits, setCachedCredits] = useState<number | null>(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState(0);
  const [draftsCount, setDraftsCount] = useState<number | null>(null);

  // Secure credit fetch with rate limiting and mutex
  const fetchCredits = useCallback(async () => {
    if (!user) return;

    const now = Date.now();
    if (now - lastFetchTimestamp < FETCH_INTERVAL) {
      return;
    }

    await creditsMutex.acquire();
    try {
      const { data, error } = await supabase
        .from('users')
        .select('credits')
        .eq('id', user.id)
        .eq('status', 'active')
        .single();

      if (error) throw error;

      const freshCredits = data?.credits ?? 0;
      setCachedCredits(freshCredits);
      setCreditBalanceCache(user.id, freshCredits);
      setLastFetchTimestamp(now);
    } catch (err) {
      console.error('Error fetching credits:', err);
      showToast('Failed to update credit balance', 'error');
    } finally {
      creditsMutex.release();
    }
  }, [user, lastFetchTimestamp, showToast]);

  // Initialize credits from cache
  useEffect(() => {
    if (!user) return;

    const cached = getCreditBalanceCache(user.id);
    if (cached !== null) {
      setCachedCredits(cached);
    }

    fetchCredits();
  }, [user, fetchCredits]);

  // Fetch drafts count
  useEffect(() => {
    if (!user) return;

    const fetchDraftsCount = async () => {
      try {
        // Check cache first
        const cachedData = getMyDraftsCache(user.id);
        if (cachedData) {
          console.log('Using cached drafts count');
          setDraftsCount(cachedData.count);
          return;
        }

        console.log('Fetching drafts count from storage');
        // Fetch from storage
        const { data, error } = await supabase
          .storage
          .from('latexstore')
          .list(`${user.id}/`, {
            limit: 100,
            offset: 0,
          });

        if (error) throw error;

        // Filter to only include .zip files and exclude .emptyFolderPlaceholder
        const filteredFiles = (data || []).filter(file =>
          file.name !== '.emptyFolderPlaceholder' && file.name.toLowerCase().endsWith('.zip')
        );

        setDraftsCount(filteredFiles.length);
        setMyDraftsCache(user.id, {
          files: filteredFiles
        });
        console.log('Drafts count cached');
      } catch (err) {
        console.error('Error fetching drafts count:', err);
        clearMyDraftsCache(user.id);
      }
    };

    fetchDraftsCount();
  }, [user]);

  // Periodic credit refresh
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(fetchCredits, FETCH_INTERVAL);
    return () => clearInterval(interval);
  }, [user, fetchCredits]);

  // Secure transaction fetch with proper error handling
  const fetchTransactions = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoading(true);
      setError(null);

      const { data: transactionData, error: fetchError } = await supabase
        .from('credit_transactions')
        .select(`
          id,
          amount,
          type,
          description,
          created_at,
          workflow_id,
          related_feature_id,
          feature:features!credit_transactions_related_feature_id_fkey (
            id,
            name,
            slug,
            group
          ),
          workflow:workflows (
            output
          )
        `)
        .eq('user_id', user.id)
        .eq('type', 'feature_use')
        .order('created_at', { ascending: false })
        .limit(10);

      if (fetchError) throw fetchError;

      const transactions = transactionData || [];
      setTransactions(transactions);

      if (isAdmin()) {
        const transactionsWithFeatures = transactions.filter(t => t.feature !== null);
        const features = transactionsWithFeatures
          .slice(0, 3)
          .map(t => {
            const mainGroup = t.feature!.group.split('-')[0].toLowerCase();
            const icon = groupIconMap[mainGroup] || DEFAULT_ICON;

            return {
              id: t.id,
              name: t.feature!.name,
              usedAt: t.created_at,
              icon
            };
          });

        setRecentFeatures(features);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transaction data');
      showToast('Failed to load recent activity', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user, isAdmin, showToast]);

  useEffect(() => {
    if (!user) return;
    fetchTransactions();
  }, [user, fetchTransactions]);

  if (!user) {
    return null;
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-semibold text-[--text-primary]">
          Welcome, {user.username}!
        </h1>

        <div className="mt-6 space-y-6">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Credit Balance Card */}
            <div className="w-full sm:w-1/2 bg-[--bg-primary] overflow-hidden shadow rounded-lg">
              <Link
                to="/credit-history"
                className="block p-5 transition-colors hover:bg-[--state-hover]"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-[rgb(var(--primary-500)/0.1)] rounded-full">
                      <CreditCard className="h-6 w-6 text-primary-500" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-[--text-secondary] truncate flex items-center">
                        My Credits
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-[--text-primary]">
                          {cachedCredits !== null ? cachedCredits.toLocaleString() : 'Loading...'}
                        </div>
                        <div className="ml-2 flex items-baseline text-sm font-medium text-[--text-secondary]">
                          credits
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </Link>
            </div>

            {/* My Drafts Card */}
            <div className="w-full sm:w-1/2 bg-[--bg-primary] overflow-hidden shadow rounded-lg">
              <Link
                to="/my-drafts"
                className="block p-5 transition-colors hover:bg-[--state-hover]"
              >
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="p-3 bg-[rgb(var(--primary-500)/0.1)] rounded-full">
                      <FileText className="h-6 w-6 text-primary-500" />
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-[--text-secondary] truncate flex items-center">
                        My Drafts
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-[--text-primary]">
                          {draftsCount !== null ? draftsCount.toLocaleString() : 'Loading...'}
                        </div>
                        <div className="ml-2 flex items-baseline text-sm font-medium text-[--text-secondary]">
                          drafts
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Favorites Section */}
          <div className="bg-[--bg-primary] overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <h3 className="text-lg leading-6 font-medium text-[--text-primary] mb-4">
                My Favorites
              </h3>
              <FavoriteFeatures />
            </div>
          </div>

          {/* Recent Features - Only shown for admin users */}
          {isAdmin() && (
            <div className="bg-[--bg-primary] overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <h3 className="text-lg leading-6 font-medium text-[--text-primary]">
                  Recent Activity
                </h3>
                <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                  {recentFeatures.length > 0 ? (
                    recentFeatures.map((feature) => {
                      const Icon = iconComponents[feature.icon as keyof typeof iconComponents] || iconComponents[DEFAULT_ICON];
                      return (
                        <div
                          key={feature.id}
                          className="relative bg-[--bg-secondary] pt-5 px-4 pb-12 sm:pt-6 sm:px-6 shadow rounded-lg overflow-hidden"
                        >
                          <dt>
                            <div className="absolute bg-primary-500 rounded-md p-3">
                              <Icon className="h-6 w-6 text-[--text-inverse]" />
                            </div>
                            <p className="ml-16 text-sm font-medium text-[--text-secondary] truncate">
                              {feature.name}
                            </p>
                          </dt>
                          <dd className="ml-16 pb-6 flex items-baseline sm:pb-7">
                            <p className="text-sm text-[--text-secondary]">
                              Last used: {new Date(feature.usedAt).toLocaleDateString()}
                            </p>
                          </dd>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-3 text-center py-6 text-[--text-secondary]">
                      No features used yet
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)] p-4 rounded-lg">
              <p className="text-sm text-error-600">{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}